import React, { ReactElement } from 'react'
import { Box, Link, Space, TextBody } from '@ally/metronome-ui'
import styled from 'styled-components'
import { formatCurrency } from '@ally/aos-utils'
import { useWidget } from './WidgetContext'
import { Booster } from '../../../Data'
import SavingsGrowthBreakdown from './SavingsGrowthBreakdown'
import SifiIcon from '../../../static-assets/images/boosters/recurring_transfers_lg.svg'
import FasIcon from '../../../static-assets/images/boosters/surprise_savings_lg.svg'
import RupIcon from '../../../static-assets/images/boosters/round_ups_lg.svg'

interface BoosterDetails {
  accountBoosterId: string
  boosterName: BoosterType
  boosterType: string
  savedSoFar: number
}

enum BoosterType {
  RUP = 'Round Ups',
  FAS = 'Surprise Savings',
  SIFI = 'Recurring Transfers',
}

function getBoosterSummary(boosters: Booster[]): BoosterDetails[] {
  const boosterTypes = [
    ...new Set(boosters.map(booster => booster.boosterType)),
  ]

  return boosterTypes
    .map(type => {
      return boosters
        .filter(booster => booster.boosterType === type)
        .reduce(
          (accum, { accountBoosterId, boosterType, cumulativeBalance }) => {
            return {
              accountBoosterId,
              boosterName: BoosterType[boosterType as keyof typeof BoosterType],
              boosterType,
              savedSoFar: accum.savedSoFar + cumulativeBalance,
            }
          },
          {
            accountBoosterId: '',
            boosterName: '' as BoosterType,
            boosterType: '',
            savedSoFar: 0,
          },
        )
    })
    .sort((a, b) => a.boosterName.localeCompare(b.boosterName))
}

export const StyledBoosterList = styled.ul`
  margin: 0;
  padding: 0;
`

export const StyledBoosterListItem = styled.li`
  ${({ theme: { space } }): string => `
    display: flex;
    justify-content: left;
    align-items: center;
    padding: ${space.xs} 0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  `}
`

export const StyledIcon = styled.span`
  ${({ theme: { space } }): string => `
    padding-right: ${space.sm};
    svg {
      max-width: 50px;
      max-height: 50px;
    }
  `}
`

export const StyledBoosterDetails = styled(Box)`
  display: inline-block;
  margin-inline-start: 0;
`

const BoosterIcon: React.FC<{ type: string }> = ({ type }) => {
  const IconLookup = {
    RUP: <RupIcon />,
    FAS: <FasIcon />,
    SIFI: <SifiIcon />,
  }
  return (
    <StyledIcon aria-hidden>
      {IconLookup[type as keyof typeof IconLookup]}
    </StyledIcon>
  )
}

const Boosters: React.FC<{
  handleRef: (el: HTMLElement | null) => number | null
}> = ({ handleRef }): ReactElement => {
  const { activeAccountId, stkSummaryData } = useWidget()
  const boosterSummary = stkSummaryData
    ? getBoosterSummary(stkSummaryData.boosters)
    : []

  return boosterSummary.length > 0 ? (
    <>
      <StyledBoosterList tabIndex={-1} ref={handleRef}>
        {boosterSummary.map(booster => (
          <StyledBoosterListItem key={booster.accountBoosterId}>
            <BoosterIcon type={booster.boosterType} />
            <StyledBoosterDetails>
              <Space pb="xxs">
                <TextBody size="sm" tag="div" weight="bold">
                  {booster.boosterName}
                </TextBody>
              </Space>
              <TextBody tag="div" size="sm">
                <TextBody size="sm" tag="span" weight="bold">
                  {formatCurrency.asCurrency(booster.savedSoFar)}
                </TextBody>
                &nbsp;saved so far!
              </TextBody>
            </StyledBoosterDetails>
          </StyledBoosterListItem>
        ))}
      </StyledBoosterList>
      <SavingsGrowthBreakdown />
    </>
  ) : (
    <>
      <Space mb="xs">
        <TextBody tag="p" size="sm" tabIndex={-1} ref={handleRef}>
          You haven&apos;t set up boosters.
        </TextBody>
      </Space>
      <Link
        text="Set up boosters on your savings account"
        to={`bank/accounts/stk-osa/${activeAccountId}/boosters`}
        withAllyTM="STK Set up boosters on your savings account Setup widget"
      />
    </>
  )
}

export default Boosters
